import React from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import * as ProcessActions from "../../redux/actions/process.actions";
import GenericForm from "../forms/GenericForm";
import LoadingForm from "../customdesign/LoadingForm";
import { saveCommentary, calculateRows, getUserInfo, saveFlowField } from "../../utils/Functions";
import Editor from "@monaco-editor/react";

/* ─────────────────────────────────────────────────────────────
   Helpers JSON
───────────────────────────────────────────────────────────── */
function isJsonValue(val) {
  if (val == null || val === "") return false;
  const s = String(val).trim();
  if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
    try { const p = JSON.parse(s); return p !== null && typeof p === "object"; } catch (_) {}
  }
  return false;
}

function useIsDark() {
  const [isDark, setIsDark] = React.useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );
  React.useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark")
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

const LINE_H = 22;   // px por línea Monaco a fontSize 15
const MAX_LINES = 6; // líneas visibles antes de scroll/expand
const COMPACT_H = MAX_LINES * LINE_H + 10; // ~130px
const FULL_H = 400;

const JsonViewerField = ({ label, value }) => {
  const isDark = useIsDark();
  const [expanded, setExpanded] = React.useState(false);
  let formatted = value;
  try { formatted = JSON.stringify(JSON.parse(value), null, 2); } catch (_) {}
  const lineCount = (formatted || "").split("\n").length;
  const needsExpand = lineCount > MAX_LINES;
  const height = needsExpand && !expanded ? COMPACT_H : expanded ? FULL_H : lineCount * LINE_H + 10;

  return (
    <div className="xmem-field xmem-field--full xmem-field--json">
      <div className="xmem-json-header">
        <span className="xmem-field-label">{label}</span>
        {needsExpand && (
          <button type="button" className="xmem-json-expand-btn" onClick={() => setExpanded((e) => !e)}>
            {expanded ? "⊟ Contraer" : "⊞ Expandir"}
          </button>
        )}
      </div>
      <div style={{ marginTop: 4, border: "1px solid var(--xflow-line, #d0d7de)", borderRadius: 6, overflow: "hidden" }}>
        <Editor
          height={height}
          defaultLanguage="json"
          value={formatted}
          theme={isDark ? "vs-dark" : "light"}
          options={{ readOnly: true, minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 15, lineNumbers: "off", wordWrap: "off", automaticLayout: true }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   FieldReadOnly — tarjeta de campo solo-lectura
───────────────────────────────────────────────────────────── */
const FieldReadOnly = ({ field }) => {
  const [expanded, setExpanded] = React.useState(false);
  const rawVal = field.value != null ? field.value : field.defaultvalue;
  const val = rawVal != null && rawVal !== "" ? String(rawVal) : null;

  if (isJsonValue(val)) {
    return <JsonViewerField label={field.label || field.name} value={val} />;
  }

  const isComment = field.name?.startsWith("comment_");
  const isLong = val && val.length > 80;
  const displayVal = isLong && !expanded ? val.slice(0, 80) + "…" : val || "—";
  const isFullWidth = isComment || (val && val.length > 55);

  return (
    <div className={"xmem-field" + (isFullWidth ? " xmem-field--full" : "")}>
      <span className="xmem-field-label">{field.label || field.name}</span>
      <span className={"xmem-field-value" + (isComment ? " xmem-field-value--mono" : "")}>
        {displayVal}
        {isLong && (
          <button
            type="button"
            className="xmem-expand-btn"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? "Ver menos" : "Ver más"}
          </button>
        )}
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MemoryFieldEdit — campo editable inline (sin Syncfusion)
   Click → input/textarea  →  blur o Enter/Ctrl+Enter guarda
   Escape cancela. Indicador visual de guardando/guardado.
───────────────────────────────────────────────────────────── */
const MemoryJsonEditor = ({ field, wfpflowid, wfptaskid, onChangeForm }) => {
  const rawVal = field.value != null ? field.value : field.defaultvalue;
  const initial = rawVal != null ? String(rawVal) : "";
  let formatted = initial;
  try { formatted = JSON.stringify(JSON.parse(initial), null, 2); } catch (_) {}
  const isDark = useIsDark();
  const [value, setValue] = React.useState(formatted);
  const [status, setStatus] = React.useState(null);
  const savedRef = React.useRef(formatted);
  const valueRef = React.useRef(formatted);

  const commit = async () => {
    const newVal = valueRef.current;
    if (newVal === savedRef.current) return;
    setStatus("saving");
    const resp = await saveFlowField(wfpflowid, wfptaskid, field.name, newVal);
    if (resp?.status !== false) {
      savedRef.current = newVal;
      onChangeForm?.({ name: field.name, value: newVal });
      setStatus("saved");
      setTimeout(() => setStatus(null), 2000);
    } else {
      const prev = savedRef.current;
      setValue(prev);
      valueRef.current = prev;
      setStatus("error");
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const handleMount = (editor) => {
    editor.onDidBlurEditorText(() => commit());
  };

  const [expanded, setExpanded] = React.useState(false);
  const lineCount = (value || "").split("\n").length;
  const needsExpand = lineCount > MAX_LINES;
  const height = needsExpand && !expanded ? COMPACT_H : expanded ? FULL_H : lineCount * LINE_H + 10;

  return (
    <div className="xmem-field xmem-field--editable xmem-field--full xmem-field--json">
      <div className="xmem-field-edit-header">
        <span className="xmem-field-label">{field.label || field.name}</span>
        <div className="xmem-field-edit-actions">
          {needsExpand && (
            <button type="button" className="xmem-json-expand-btn" onClick={() => setExpanded((e) => !e)}>
              {expanded ? "⊟ Contraer" : "⊞ Expandir"}
            </button>
          )}
          {status === "saving" && <span className="xmem-save-status xmem-save-status--saving">guardando…</span>}
          {status === "saved" && <span className="xmem-save-status xmem-save-status--saved">✓ guardado</span>}
          {status === "error" && <span className="xmem-save-status xmem-save-status--error">✕ error</span>}
        </div>
      </div>
      <div style={{ marginTop: 4, border: "1px solid var(--xflow-line, #d0d7de)", borderRadius: 6, overflow: "hidden" }}>
        <Editor
          height={height}
          defaultLanguage="json"
          value={value}
          theme={isDark ? "vs-dark" : "light"}
          options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 15, lineNumbers: "off", wordWrap: "off", automaticLayout: true, tabSize: 2 }}
          onChange={(val) => { valueRef.current = val ?? ""; setValue(val ?? ""); }}
          onMount={handleMount}
        />
      </div>
    </div>
  );
};

const MemoryFieldEdit = ({ field, wfpflowid, wfptaskid, onChangeForm }) => {
  const rawVal = field.value != null ? field.value : field.defaultvalue;
  const initial = rawVal != null ? String(rawVal) : "";

  if (isJsonValue(initial)) {
    return <MemoryJsonEditor field={field} wfpflowid={wfpflowid} wfptaskid={wfptaskid} onChangeForm={onChangeForm} />;
  }

  const isComment = !!field.name?.startsWith("comment_");
  const isLong = isComment || field.type === "textarea" || initial.length > 60;
  const isFullWidth = isLong || initial.length > 55;

  const [editing, setEditing] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState(initial);
  const [status, setStatus] = React.useState(null); // null | "saving" | "saved" | "error"
  const inputRef = React.useRef(null);
  const savedRef = React.useRef(initial);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      const len = inputRef.current.value.length;
      try { inputRef.current.setSelectionRange(len, len); } catch (_) {}
    }
  }, [editing]);

  const commit = async () => {
    const newVal = inputRef.current ? inputRef.current.value : displayValue;
    setEditing(false);
    if (newVal === savedRef.current) return;
    setDisplayValue(newVal);
    setStatus("saving");
    const resp = await saveFlowField(wfpflowid, wfptaskid, field.name, newVal);
    if (resp?.status !== false) {
      savedRef.current = newVal;
      onChangeForm?.({ name: field.name, value: newVal });
      setStatus("saved");
      setTimeout(() => setStatus(null), 2000);
    } else {
      setDisplayValue(savedRef.current);
      setStatus("error");
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDisplayValue(savedRef.current);
  };

  return (
    <div
      className={
        "xmem-field xmem-field--editable" +
        (isFullWidth ? " xmem-field--full" : "") +
        (editing ? " xmem-field--editing" : "")
      }
    >
      <div className="xmem-field-edit-header">
        <span className="xmem-field-label">{field.label || field.name}</span>
        <div className="xmem-field-edit-actions">
          {!editing && status === null && (
            <button
              type="button"
              className="xmem-edit-trigger"
              onClick={() => setEditing(true)}
              title="Editar"
            >
              ✎
            </button>
          )}
          {status === "saving" && (
            <span className="xmem-save-status xmem-save-status--saving">guardando…</span>
          )}
          {status === "saved" && (
            <span className="xmem-save-status xmem-save-status--saved">✓ guardado</span>
          )}
          {status === "error" && (
            <span className="xmem-save-status xmem-save-status--error">✕ error</span>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="xmem-edit-confirm"
                onMouseDown={(e) => { e.preventDefault(); commit(); }}
                title="Guardar (Enter)"
              >✓</button>
              <button
                type="button"
                className="xmem-edit-cancel"
                onMouseDown={(e) => { e.preventDefault(); cancel(); }}
                title="Cancelar (Esc)"
              >✕</button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        isLong ? (
          <textarea
            ref={inputRef}
            className="xmem-field-input xmem-field-input--area"
            defaultValue={displayValue}
            rows={isComment ? 5 : 3}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && e.ctrlKey) commit();
            }}
            onBlur={commit}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="xmem-field-input"
            defaultValue={displayValue}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") cancel();
            }}
            onBlur={commit}
          />
        )
      ) : (
        <span
          className={
            "xmem-field-value xmem-field-value--clickable" +
            (isComment ? " xmem-field-value--mono" : "")
          }
          onClick={() => setEditing(true)}
          title="Click para editar"
        >
          {displayValue || "—"}
        </span>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MemoryPanel — separa campos en solo-lectura y editables,
   ordena ambos grupos A–Z y los presenta en diseño moderno.
───────────────────────────────────────────────────────────── */
const MemoryPanel = ({
  fields,
  defConfig,
  defLang,
  wfpflowid,
  wfptaskid,
  varSchema,
  onChangeForm,
  allFields,
}) => {
  // Deduplica variables por nombre (la fuente WFP_TMP_FLOWS_VARS puede traer
  // filas repetidas del mismo var para el flujo → el form se inflaba y se veían
  // valores repetidos). Se conserva la ÚLTIMA ocurrencia (valor más reciente).
  const uniqueFields = React.useMemo(() => {
    if (!fields || fields.length === 0) return [];
    const map = new Map();
    fields.forEach((f) => map.set(f && f.name != null ? f.name : f, f));
    return Array.from(map.values());
  }, [fields]);

  const { readOnly, editable } = React.useMemo(() => {
    const ui = getUserInfo();
    const isAdmin = ui?.USERGROUPID === 1;
    const fields = isAdmin
      ? uniqueFields
      : uniqueFields.filter((f) => {
          const v = f.value != null ? f.value : f.defaultvalue;
          return !isJsonValue(v != null ? String(v) : "");
        });
    if (!fields || fields.length === 0) return { readOnly: [], editable: [] };

    const fieldIsJson = (f) => {
      const v = f.value != null ? f.value : f.defaultvalue;
      return isJsonValue(v != null ? String(v) : "");
    };

    const byLabel = (a, b) => {
      const aJson = fieldIsJson(a), bJson = fieldIsJson(b);
      if (aJson !== bJson) return aJson ? 1 : -1;
      return (a.label || a.name || "").localeCompare(b.label || b.name || "", "es", { sensitivity: "base" });
    };

    if (!varSchema || varSchema.length === 0) {
      return { readOnly: [...fields].sort(byLabel), editable: [] };
    }

    const ro = [];
    const ed = [];

    fields.forEach((field) => {
      const entry = varSchema.find((s) => s.VARNAME === field.name);
      let canEdit = false;
      if (entry) {
        const opt = entry.EDITABLE;
        if (opt === "ALL") canEdit = true;
        else if (opt !== "NO") {
          canEdit = opt === ui?.USERGROUP || ui?.USERGROUPID === 1;
        }
      }
      (canEdit ? ed : ro).push(field);
    });

    return { readOnly: ro.sort(byLabel), editable: ed.sort(byLabel) };
  }, [uniqueFields, varSchema]);

  if (!fields || fields.length === 0) {
    return (
      <div className="xmem-empty">
        <span className="xmem-empty-icon">◈</span>
        <p>{defConfig[defLang]?.nomemorymessage || "Sin datos de memoria"}</p>
      </div>
    );
  }

  return (
    <div className="xmem-panel">
      {readOnly.length > 0 && (
        <div className="xmem-section">
          <div className="xmem-section-header">
            <span className="xmem-section-title">
              {defConfig[defLang]?.memorylabel || "Datos del flujo"}
            </span>
            <span className="xmem-badge">{readOnly.length}</span>
          </div>
          <div className="xmem-grid">
            {readOnly.map((field, index) => (
              <FieldReadOnly key={(field.name || "f") + "__" + index} field={field} />
            ))}
          </div>
        </div>
      )}

      {editable.length > 0 && (
        <div className="xmem-section xmem-section--edit">
          <div className="xmem-section-header">
            <span className="xmem-section-title">
              {defConfig[defLang]?.editablefieldslabel || "Campos editables"}
            </span>
            <span className="xmem-badge xmem-badge--edit">{editable.length}</span>
          </div>
          <div className="xmem-grid">
            {editable.map((field, index) => (
              <MemoryFieldEdit
                key={(field.name || "f") + "__" + index}
                field={field}
                wfpflowid={wfpflowid}
                wfptaskid={wfptaskid}
                onChangeForm={onChangeForm}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Accordion — reemplaza AccordionComponent de Syncfusion
   Cada panel arranca cerrado excepto el primero.
───────────────────────────────────────────────────────────── */
const Accordion = ({ items, defConfig, defLang, varSchema, onChangeForm, aditionalFlowData }) => {
  const [open, setOpen] = React.useState([0]);
  const toggle = (i) =>
    setOpen((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );

  return (
    <div className="xfd-accordion">
      {items.map((flow, i) => {
        const isOpen = open.includes(i);
        const headerText =
          flow.header ||
          (defConfig[defLang].memorylabel +
            " - " +
            (flow.label && flow.key
              ? `${flow.label} ${flow.key} ${
                  process.env.REACT_APP_CUSTOM_TASKLABEL || "Tarea"
                }: ${flow.wfpflowid})`
              : flow.header || ""));

        return (
          <div
            key={i}
            className={"xfd-accordion-item" + (isOpen ? " xfd-accordion-open" : "")}
          >
            <button
              type="button"
              className="xfd-accordion-trigger"
              onClick={() => toggle(i)}
            >
              <span className="xfd-accordion-title">{headerText}</span>
              <span
                className={
                  "e-icons " + (isOpen ? "e-chevron-up" : "e-chevron-down")
                }
              ></span>
            </button>
            {isOpen && (
              <div className="xfd-accordion-body">
                <MemoryPanel
                  fields={flow.data}
                  defConfig={defConfig}
                  defLang={defLang}
                  wfpflowid={flow.wfpflowid}
                  wfptaskid={flow.wfptaskid}
                  varSchema={varSchema}
                  onChangeForm={onChangeForm}
                  allFields={aditionalFlowData}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MemorySection — bloque de memoria / historial de flujo
───────────────────────────────────────────────────────────── */
const MemorySection = ({
  flowData,
  isParent,
  loading,
  defConfig,
  defLang,
  wfpflowid,
  wfptaskid,
  varSchema,
  onChangeForm,
}) => {
  if (flowData.length > 0) {
    return !isParent ? (
      <MemoryPanel
        fields={flowData}
        defConfig={defConfig}
        defLang={defLang}
        wfpflowid={wfpflowid}
        wfptaskid={wfptaskid}
        varSchema={varSchema}
        onChangeForm={onChangeForm}
      />
    ) : (
      <Accordion
        items={flowData}
        defConfig={defConfig}
        defLang={defLang}
        varSchema={varSchema}
        onChangeForm={onChangeForm}
        aditionalFlowData={flowData}
      />
    );
  }

  return loading ? (
    <LoadingForm showMoreRows={true} />
  ) : (
    <div className="xmem-empty">
      <span className="xmem-empty-icon">◈</span>
      <p>{defConfig[defLang]?.nomemorymessage || "[Sin datos de memoria]"}</p>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   DialogExecuteTask — panel lateral sin ninguna dependencia de
   Syncfusion. Slide desde la derecha, backdrop con blur.
   Mobile: ocupa el 100% del viewport.
───────────────────────────────────────────────────────────── */
const DialogExecuteTask = (props) => {
  const { defConfig, defLang } = useSelector((state) => state.Login);
  const { requesting } = useSelector((state) => state.Process);

  const dispatch = useDispatch();

  const [params, setParams] = React.useState({});
  const [isExternalFrame, setIsExternalFrame] = React.useState(false);
  const [prevTaskComment, setPrevTaskComment] = React.useState("");
  const [isFrame, setIsFrame] = React.useState(false);
  const [isGrid, setIsGrid] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  // Tab activo del body: "form" (formTask, lo accionable) | "memory" (contexto).
  // Siempre arranca en "form" para dar prioridad a lo que importa.
  const [activeTab, setActiveTab] = React.useState("form");

  const commentRef = React.useRef(null);
  // dimensiones capturadas 1 vez (igual que original)
  const wHeight = React.useRef(window.innerHeight).current;
  const clientWidth = React.useRef(window.innerWidth).current;

  /* ── lock body scroll al abrir (scrollbar-gutter:stable evita el jitter) ── */
  React.useEffect(() => {
    if (!props.status) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [props.status]);

  /* ── cerrar con Escape ── */
  React.useEffect(() => {
    if (!props.status) return;
    const onKey = (e) => {
      if (e.key === "Escape") props.close?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.status]);

  /* ── listener de iframe: ref-based, un solo listener en toda la vida del componente ── */
  const iframeCallbacksRef = React.useRef({ close: props.close, refreshData: props.refreshData, status: props.status });
  React.useEffect(() => {
    iframeCallbacksRef.current = { close: props.close, refreshData: props.refreshData, status: props.status };
  });
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data !== "TAREA_COMPLETADA") return;
      if (!iframeCallbacksRef.current.status) return;
      iframeCallbacksRef.current.close?.();
      // pequeño delay para que close procese antes del refresh
      setTimeout(() => iframeCallbacksRef.current.refreshData?.(), 80);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []); // solo se agrega UNA vez al montar

  /* ── resetear tab SOLO cuando cambia la tarea (nuevo props.id) ── */
  React.useEffect(() => {
    setActiveTab("form");
  }, [props.id]);

  /* ── inicializar formulario cuando cambian los campos de la tarea ── */
  React.useEffect(() => {
    let newParams = {};
    const commentPos = props.flowData?.findIndex(
      (item) => item.name === `comment_${props.taskName}`
    );

    /* reset flags de tipo de campo */
    setIsGrid(false);
    setIsFrame(false);
    setIsExternalFrame(false);

    props.data?.forEach((field) => {
      if (field.type === "grid") setIsGrid(true);
      if (field.type === "iframe") setIsFrame(true);
      if (field.type === "iframe" && field.iframetype === "EXTERNAL")
        setIsExternalFrame(true);
      newParams[field.name] = field.value || field.defaultvalue || "";
    });

    setParams(newParams);

    if (props.id) {
      setTimeout(() => {
        if (commentPos >= 0 && props.flowData[commentPos]?.value) {
          setPrevTaskComment(props.flowData[commentPos].value);
        }
        setIsLoading(false);
      }, 50);
    } else {
      setIsLoading(false);
    }
  }, [props.id, props.data, props.flowData, props.taskName]);

  /* ── submit ── */
  const submitForm = async (action) => {
    if (action === "NO___OPTION") {
      const data = {
        id: props.id,
        commentary: commentRef.current?.value || "",
      };
      const response = await saveCommentary(data);
      if (response.status) props.close?.();
      dispatch(ProcessActions.setMessage(response));
      return;
    }

    /* agregar comentario al payload */
    const commentVal = commentRef.current?.value || "";
    if (commentVal.length > 0) {
      const date = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const formattedDate = `${pad(date.getDate())}/${pad(
        date.getMonth() + 1
      )}/${date.getFullYear()} ${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}`;
      params["comment_" + props.taskName] =
        prevTaskComment + formattedDate + ":" + commentVal + "\n";
    }

    /* limpiar archivos sin cambios y serializar grids */
    for (let i in props.data) {
      const field = props.data[i];
      if (
        field.type === "file" &&
        field.defaultvalue &&
        field.defaultvalue !== "" &&
        params[field.name] === field.defaultvalue
      ) {
        params[field.name] = "";
      }
      if (field.type === "grid") {
        try {
          if (Array.isArray(params[field.name]))
            params[field.name] = JSON.stringify(params[field.name]);
        } catch {
          params[field.name] = "[]";
        }
      }
    }

    const data = props.isParent
      ? { parentid: props.id, isParent: true, params, optionValue: action }
      : { id: props.id, isParent: false, params, optionValue: action };

    props.close?.();
    dispatch(ProcessActions.executeTask(data));
    props.clearTaskId?.();
  };

  const onChange = (event) => {
    setParams((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  /* ── chips de contexto para el header ──────────────────────────────────────
     Fuente de verdad del orden: varSchema (header API) ordenado por SORTORDER.
     El campo[0] ya es el título — chips = campos[1..N] con valor no vacío,
     máximo 2. El valor se busca en flowData por VARNAME.
  ── */
  const headerChips = React.useMemo(() => {
    const schema = props.varSchema || [];
    if (!schema.length) return [];

    // mapa nombre→valor desde flowData (dedup: primera aparición gana)
    const flat = props.isParent
      ? (props.flowData || []).flatMap(item => item.data || [])
      : (props.flowData || []);
    const valueMap = new Map();
    flat.forEach(f => {
      if (!f?.name || valueMap.has(f.name)) return;
      const v = f.value != null ? f.value : f.defaultvalue;
      if (v != null && String(v).trim() !== "") valueMap.set(f.name, String(v));
    });

    // header ordenado por SORTORDER → saltar el primero → tomar hasta 2 con valor
    const sorted = [...schema].sort((a, b) => (a.SORTORDER ?? 0) - (b.SORTORDER ?? 0));
    const chips = [];
    for (let i = 1; i < sorted.length && chips.length < 2; i++) {
      const entry = sorted[i];
      const val = valueMap.get(entry.VARNAME);
      if (!val) continue;
      chips.push({
        label: entry.VARLABEL || entry.VARNAME,
        value: val,
        remarkable: entry.REMARKABLE === "YES",
      });
    }
    return chips;
  }, [props.flowData, props.varSchema, props.isParent]);

  /* ── no render cuando no está abierto o cargando ── */
  if (!props.status || isLoading) return null;

  const isMobile = clientWidth < 799;
  const hasData = props.data?.length > 0;
  // Unica accion posible = guardar comentario (tarea sin salida en el diseno).
  const isOnlySaveComment =
    props.actions?.length === 1 && props.actions[0] === "NO___OPTION";

  /* ── Paneles del body ──────────────────────────────────────────────
     formTask (lo accionable) y memoria (contexto). Se muestran como tabs
     cuando ambos existen; si solo hay uno, se muestra a pantalla completa.
     Ambos paneles se mantienen MONTADOS (toggle por CSS) para no recargar
     el iframe ni perder lo que el usuario haya escrito al cambiar de tab. */
  const hasForm = hasData;
  const hasMemory = (props.flowData?.length || 0) > 0;
  const showTabs = hasForm && hasMemory;
  const currentTab = !hasForm ? "memory" : !hasMemory ? "form" : activeTab;

  const memoryNode = (
    <MemorySection
      flowData={props.flowData}
      isParent={props.isParent}
      loading={props.loading}
      defConfig={defConfig}
      defLang={defLang}
      wfpflowid={props.wfpflowid}
      wfptaskid={props.wfptaskid}
      varSchema={props.varSchema}
      onChangeForm={props.onChangeForm}
    />
  );

  const formNode = hasForm ? (
    <GenericForm
      data={props.data}
      aditionalFlowData={props.flowData}
      iFrameHeight={isMobile ? Math.max(wHeight - 200, 480) : Math.max(wHeight - 250, 460)}
      defConfig={defConfig}
      defLang={defLang}
      wfpflowid={props.wfpflowid}
      wfptaskid={props.wfptaskid}
      officecode={props.officecode}
      datasubtitle={defConfig[defLang].taskformtitle}
      datapostload={props.datapostload}
      flowactions={props.actions}
      onChange={onChange}
      buttons={[]}
      expandaction={!isExternalFrame ? () => {} : null}
      collapseaction={() => {}}
    />
  ) : null;

  const formTabLabel = defConfig[defLang].taskformtitle || "Formulario";
  const memoryTabLabel = defConfig[defLang].memorylabel || "Memoria";
  const drawerWidth = isMobile
    ? "100%"
    : isExternalFrame || !hasData
    ? "56%"
    : "82%";

  return ReactDOM.createPortal(
    /* ── Overlay backdrop ── */
    <div
      className="xfd-overlay"
      onClick={props.close}
      role="dialog"
      aria-modal="true"
    >
      {/* ── Panel lateral ── */}
      <div
        className="xfd-drawer"
        style={{ width: drawerWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="xfd-header">
          <div className="xfd-header-title">
            <span className="e-icons e-group-2 xfd-header-icon"></span>
            <div>
              <h2 className="xfd-header-text">{props.header}</h2>
              {headerChips.length > 0 && (
                <div className="xfd-header-chips">
                  {headerChips.map((chip, i) => (
                    <span
                      key={i}
                      className={"xfd-header-chip" + (chip.remarkable ? " xfd-header-chip--remarkable" : "")}
                    >
                      <span className="xfd-header-chip-label">{chip.label}:</span>
                      <span className="xfd-header-chip-value">{chip.value}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="xfd-close-btn"
            onClick={props.close}
            title="Cerrar"
          >
            <span className="e-icons e-close"></span>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="xfd-body">

          {/* Selector de tabs: solo cuando coexisten formTask + memoria */}
          {showTabs && (
            <div className="xfd-tabs" role="tablist" aria-label="Secciones de la tarea">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === "form"}
                className={"xfd-tab" + (currentTab === "form" ? " xfd-tab-active" : "")}
                onClick={() => setActiveTab("form")}
              >
                <span className="e-icons e-edit-2 xfd-tab-icon"></span>
                <span className="xfd-tab-label">{formTabLabel}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === "memory"}
                className={"xfd-tab" + (currentTab === "memory" ? " xfd-tab-active" : "")}
                onClick={() => setActiveTab("memory")}
              >
                <span className="e-icons e-folder-open xfd-tab-icon"></span>
                <span className="xfd-tab-label">{memoryTabLabel}</span>
              </button>
            </div>
          )}

          {/* Paneles: ambos montados; visibilidad por CSS según el tab activo */}
          <div className={"xfd-tab-panels" + (showTabs ? " xfd-tab-panels-tabbed" : "")}>
            {hasForm && (
              <div
                role="tabpanel"
                className={
                  "xfd-section xfd-section-form xfd-tab-panel" +
                  (currentTab === "form" ? " xfd-tab-panel-active" : "")
                }
              >
                {formNode}
              </div>
            )}
            {hasMemory || !hasForm ? (
              <div
                role="tabpanel"
                className={
                  "xfd-section xfd-section-memory xfd-tab-panel" +
                  (currentTab === "memory" ? " xfd-tab-panel-active" : "")
                }
              >
                {memoryNode}
              </div>
            ) : null}
          </div>

          {/* ── Sección de comentarios ── */}
          {!isExternalFrame && (
            <div className="xfd-comments">
              <textarea
                ref={commentRef}
                rows={2}
                className="xfd-textarea"
                placeholder={defConfig[defLang].aditionalcommentary}
              />
              {prevTaskComment?.length > 0 && (
                <div className="xfd-comment-history">
                  <span className="xfd-comment-history-label">
                    {defConfig[defLang].commenthistory}
                  </span>
                  <textarea
                    rows={calculateRows(
                      prevTaskComment,
                      clientWidth > 799 ? clientWidth * 0.7 : clientWidth - 100
                    )}
                    value={prevTaskComment}
                    readOnly
                    className="xfd-textarea xfd-textarea-readonly"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer con acciones ── */}
        {!isExternalFrame && props.actions?.length > 0 && (
          <div className="xfd-footer">
            <button
              type="button"
              className="xfd-cancel-btn"
              onClick={props.close}
            >
              <span className="e-icons e-close"></span>
              {defConfig[defLang].cancellabel || "Cancelar"}
            </button>
            {/* NO___OPTION = la tarea no tiene transicion de salida en el
                diseno del flujo (sin filas en WFP_TMP_TASK_OPTIONS), asi que
                submitForm() solo puede guardar el comentario. Rotularlo
                "Aceptar" con icono de check hacia creer que ejecutaba y
                avanzaba el flujo — de ahi el "guarda el comentario y no hace
                nada". Se rotula por lo que realmente hace y se avisa por que. */}
            {isOnlySaveComment && (
              <span className="xfd-footer-hint">
                <span className="e-icons e-circle-info"></span>
                {defConfig[defLang].nooptionhintlabel}
              </span>
            )}
            <div className="xfd-footer-actions">
              {props.actions.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  className="xfd-action-btn"
                  onClick={() => submitForm(opt)}
                >
                  <span
                    className={
                      "e-icons " + (opt === "NO___OPTION" ? "e-comment-show" : "e-check")
                    }
                  ></span>
                  {opt === "NO___OPTION"
                    ? defConfig[defLang].savecommentonlylabel ||
                      defConfig[defLang].acceptlabel
                    : opt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

DialogExecuteTask.defaultProps = {
  flowData: [],
  data: [],
  actions: [],
};

export default DialogExecuteTask;
